<script lang="ts" setup>
import { ref, onMounted } from "vue";
import useDateTimeFormatter from "@/composables/formatDateTime.js";
import { getBuildingMeasures } from "@/services/api/chart.api";
import ChartAppliedStatus from "@/components/app/charts/ChartAppliedStatus.vue";
import ChartMeasures from "@/components/app/charts/ChartMeasures.vue";
import { useToast } from "vue-toastification";

const toaster = useToast();
const { formatDateApi } = useDateTimeFormatter();

const props = defineProps({
  token: {
    type: String,
    default: "",
  },
  compact: {
    type: Boolean,
    default: false,
  },
  certified: {
    type: Boolean,
    default: true,
  },
  refDate: {
    type: Date,
  },
});

const chart = ref({
  status: {},
  any: undefined,
});

const isLoading = ref(false);

onMounted(() => {
  fetchBuildingMeasuresData();
});

async function fetchBuildingMeasuresData() {
  isLoading.value = true;
  const response = await getBuildingMeasures(props.token, props.certified, formatDateApi(props.refDate));
  if (response) {
    // chart.value = response.data;
    // chart.value = { ...chart.value, ...response.data }; // Spread to preserve reactivity
    chart.value = JSON.parse(JSON.stringify(response.data)); // Forces reactivity by creating a new object
  } else {
    toaster.error("Ophalen van de maatregelen data is mislukt!");
  }
  isLoading.value = false;
}
</script>

<template>
  <div class="widget-item" :class="{ 'widget-item--compact': props.compact }">
    <header>
      <slot name="header"></slot>
      <div class="widget-actions">
        <router-link :to="{ name: 'AppliedOverview' }" class="widget-link">Bekijk alle maatregelen</router-link>
        <information-dropdown>
          <slot name="header"></slot>
          <slot name="infotext"></slot>
        </information-dropdown>
      </div>
    </header>

    <loader-spinner v-if="isLoading === true" :is-loading="isLoading" :min-height="200"></loader-spinner>
    <!-- Charts werken niet als ze in een v-if constructie zitten -->
    <div v-if="isLoading === false && chart.any == true">
      <div class="widget-wrapper">
        <div class="widget-chart applied-measures">
          <chart-measures :source-data="chart" :height="props.compact ? 96 : 150"></chart-measures>
        </div>
        <div class="widget-chart building-measures">
          <chart-applied-status
            :showInnerLabel="true"
            :key="`doughnutChartBuildingMeasures${props.certified}`"
            :chartName="`BuildingMeasures${props.certified}`"
            :radius="props.compact ? 126 : 200"
            :source-data="chart.status"
          ></chart-applied-status>
        </div>
      </div>
    </div>
    <div v-if="isLoading === false && chart.any == false">
      <slot name="nomeasurestext"></slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.widget-item--compact .widget-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  margin-top: 4px;
}

.widget-item--compact .widget-chart.applied-measures {
  flex: 0 0 auto;
}

.widget-item--compact .widget-chart.building-measures {
  flex: 1;
  min-height: 0;
  margin-top: 4px;
}

.widget-item--compact .widget-chart.building-measures :deep(.advanced-doughnut) {
  flex-wrap: nowrap;
  align-items: flex-start;
}

.widget-item--compact .widget-chart.building-measures :deep(.doughnut) {
  flex: 0 0 auto;
}

.widget-item--compact .widget-chart.building-measures :deep(.vue-chart-legend) {
  margin: 4px;
  width: 156px;
}

.widget-item--compact .widget-chart.building-measures :deep(.vue-chart-legend li) {
  min-width: 0;
  margin-bottom: 3px;
}

.widget-item--compact .widget-chart.building-measures :deep(.chart-legend-label-text) {
  font-size: 0.72rem;
  line-height: 1.1;
}

.widget-item--compact .widget-chart.building-measures :deep(.chart-percentage) {
  font-size: 0.72rem;
}

:deep(.vue-chart .vue-chart-legend) {
  font-size: 10pt;
  margin-top: -5px;
}
</style>
